import { registerWithInviteApiV1AuthRegisterWithInvitePost } from '@/common/api/client/sdk.gen'
import type {
    RegisterWithInviteApiV1AuthRegisterWithInvitePostData,
    RegisterWithInviteApiV1AuthRegisterWithInvitePostError,
    RegisterWithInviteApiV1AuthRegisterWithInvitePostResponse,
} from '@/common/api/client/types.gen'
import { useFormMutation } from '@/common/hooks/useFormMutation'
import { TelegramPixel } from '@/common/lib/telegramPixel'
import { YandexMetrica } from '@/common/lib/yandexMetrica'
import { useNavigate } from 'react-router-dom'
import { omitRegistrationUiFields } from '../lib/utils'
import { ROUTES } from '../lib/constants'
import { setHeaderToken } from '@/common/lib/fetch'
import { useQueryClient } from '@tanstack/react-query'

type RegisterWithInviteFormData = RegisterWithInviteApiV1AuthRegisterWithInvitePostData['body'] & {
    terms_of_service?: boolean
}

const clearAuthState = () => {
    localStorage.removeItem('current_org_id')
}

export const useRegisterWithInvite = ({ inviteToken }: { inviteToken: string }) => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { mutation, register, handleSubmit, errors, onSubmit, setError } = useFormMutation<
        RegisterWithInviteFormData,
        RegisterWithInviteApiV1AuthRegisterWithInvitePostResponse,
        RegisterWithInviteApiV1AuthRegisterWithInvitePostError
    >({
        mutationFn: data =>
            registerWithInviteApiV1AuthRegisterWithInvitePost({
                body: omitRegistrationUiFields(data),
            }).then(r => r.data!),
        formOptions: {
            defaultValues: {
                invite_token: inviteToken,
                password: '',
                password_confirm: '',
                terms_of_service: false,
                subscribe_marketing: false,
            },
        },
        toastOptions: {
            successMessage: 'Регистрация успешна',
            errorMessage: 'Ошибка при регистрации',
        },
        onSuccess: data => {
            YandexMetrica.reachGoal('reg_success')
            TelegramPixel.trackRegistrationSuccess()
            queryClient.clear()
            clearAuthState()
            const auth = data.auth || null
            if (auth) {
                setHeaderToken(auth.access_token, auth.refresh_token)
            }
            navigate(ROUTES.APP)
        },
        onError: () => {
            setError('root', { type: 'server', message: 'Ошибка при регистрации' })
        },
    })

    return {
        mutation,
        register,
        handleSubmit,
        errors,
        onSubmit,
    }
}
